import { useEffect, useRef, useState } from 'react';

import { useLanguage } from '../i18n/language.js';

type StoryStep = 0 | 1 | 2 | 3;
type ComposerPhase = 'input' | 'processing' | 'profile';

const STORY = {
  en: {
    heading: 'From your business to content that fits.',
    sub: 'Mouja learns what you do, what works and what feels right—then turns it into a steady stream of content.',
    stepsLabel: 'Product story steps',
    steps: [
      'We understand your business.',
      'We find what works in your niche.',
      'You show us what feels right.',
      'Your Production Line delivers.',
    ],
    aria: [
      'Step 1 of 4. A miniature Mouja Composer attaches a website and two social sources, then becomes a short Business Profile.',
      'Step 2 of 4. A wall of vertical videos from the same niche is analyzed and three useful patterns surface.',
      'Step 3 of 4. One vertical candidate appears at a time while like, dislike, skip, and previous choices form this line’s taste.',
      'Step 4 of 4. Profile, niche, and taste enter a physical Production Line and a finished video reaches the delivery rack.',
    ],
    fixture: 'Demonstration business · Bayt al-Oud',
    composer: {
      prompt: 'Optional note — pick Website, Instagram or TikTok to attach your link',
      attach: 'Press ↵ to attach',
      cta: 'Build my business profile',
      next: 'Mouja will analyze these sources and create a profile for you to review before signing in.',
      tabs: ['Prompt', 'Website', 'Instagram', 'TikTok'],
      chips: ['bayt-aloud.sa', 'instagram.com/bayt.aloud', 'tiktok.com/@baytaloud'],
      building: 'Building your profile',
      processing: 'Preparing your Business Profile',
      keepOpen: 'Keep this open — Mouja finishes on its own.',
      ready: 'Your business profile is ready',
      business: 'Bayt al-Oud',
      businessAr: 'بيت العود',
      offer: 'Travel-size oud and attar for people who fly with their scent.',
      market: 'Riyadh · Saudi Arabia',
      offersLabel: 'Offers found',
      offers: ['Travel oud 12 ml', 'Attar sets', 'Burner kits'],
      sources: 'Built from your website, Instagram and TikTok.',
      ask: 'Does this look like your business?',
      confirm: 'Confirm business profile',
      wrong: 'Something’s not right',
    },
    patterns: ['Strong hook', 'Fast pacing', 'Clear offer'],
    tasteLabel: 'This line’s taste',
    taste: ['Fast opening', 'Natural light', 'Minimal text'],
    candidate: 'Hands, one take',
    metrics: ['1.2M views', '84K likes', '3.4K comments'],
    cue: 'Merchant liked',
    inputs: ['Profile', 'Niche', 'Taste'],
    direction: 'One take · daylight',
    active: 'Active',
    cadence: '1 video / day',
    rack: 'Delivery rack',
  },
  ar: {
    heading: 'من عملك إلى محتوى يناسبه.',
    sub: 'تتعلّم موجة ما تعمله، وما ينجح، وما يناسب ذوقك — ثم تحوّله إلى تدفّق ثابت من المحتوى.',
    stepsLabel: 'خطوات قصة المنتج',
    steps: [
      'نفهم عملك.',
      'نجد ما ينجح في سوقك.',
      'أنت تُرينا ما يناسبك.',
      'خط إنتاجك يُسلّم.',
    ],
    aria: [
      'الخطوة ١ من ٤. المُنشئ المصغّر يرفق الموقع ومصدر اجتماعي، وبعدها يصير ملف نشاط مختصر.',
      'الخطوة ٢ من ٤. جدار فيديوهات رأسية من نفس سوقك ينقرأ، وتطلع ثلاثة أنماط تنفعك.',
      'الخطوة ٣ من ٤. يجيك مرشّح رأسي واحد كل مرة، وقرارات الإعجاب والرفض والتخطي والرجوع تبني ذوق هذا الخط.',
      'الخطوة ٤ من ٤. الملف والسوق والذوق يدخلون خط الإنتاج، ويوصل فيديو جاهز على رف التسليم.',
    ],
    fixture: 'نشاط تجريبي · بيت العود',
    composer: {
      prompt: 'وصف اختياري — اختر تبويب الرابط اللي تبي ترفقه',
      attach: 'اضغط ↵ عشان ينضاف',
      cta: 'سوّ لي ملف نشاطي',
      next: 'موجة بتقرأ هذي المصادر وتسوّي لك ملف تشوفه قبل ما تسجّل دخولك.',
      tabs: ['Prompt', 'Website', 'Instagram', 'TikTok'],
      chips: ['bayt-aloud.sa', 'instagram.com/bayt.aloud', 'tiktok.com/@baytaloud'],
      building: 'نبني ملفك',
      processing: 'نجهّز ملفك التجاري',
      keepOpen: 'خلها مفتوحة — موجة تكمّل بروحها.',
      ready: 'ملفك التجاري جاهز',
      business: 'بيت العود',
      businessAr: '',
      offer: 'عود ودهن عود بحجم السفر للي يسافر وريحته معه.',
      market: 'الرياض · السعودية',
      offersLabel: 'العروض اللي لقيناها',
      offers: ['عود سفر ١٢ مل', 'أطقم دهن عود', 'أطقم مباخر'],
      sources: 'مبني من موقعك وInstagram وTikTok.',
      ask: 'هذا نشاطك صح؟',
      confirm: 'أكّد الملف التجاري',
      wrong: 'فيه شي مو صحيح',
    },
    patterns: ['افتتاحية قوية', 'إيقاع سريع', 'عرض واضح'],
    tasteLabel: 'ذوق هذا الخط',
    taste: ['افتتاحية سريعة', 'ضوء طبيعي', 'نص قليل'],
    candidate: 'أيدٍ، مشهد واحد',
    metrics: ['١٫٢ مليون مشاهدة', '٨٤ ألف إعجاب', '٣٫٤ ألف تعليق'],
    cue: 'أعجب التاجر',
    inputs: ['الملف', 'السوق', 'الذوق'],
    direction: 'مشهد واحد · ضوء نهار',
    active: 'نشِط',
    cadence: 'فيديو واحد / يوم',
    rack: 'رفّ التسليم',
  },
} as const;

const SOURCE_MARKS = ['is-web', 'is-instagram', 'is-tiktok'] as const;

function SparkGlyph(): React.ReactElement {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="m12 2.6 1.9 5.6 5.6 1.9-5.6 1.9-1.9 5.6-1.9-5.6-5.6-1.9 5.6-1.9Z"
        fill="currentColor"
      />
      <circle cx="19.4" cy="4.7" r="1.5" fill="#a30089" />
      <circle cx="5.1" cy="18.4" r="1.1" fill="#2e3cff" />
    </svg>
  );
}

function MiniComposer({
  copy,
  phase,
}: {
  copy: (typeof STORY)[keyof typeof STORY]['composer'];
  phase: ComposerPhase;
}): React.ReactElement {
  return (
    <div className="mj-story-composer" data-phase={phase}>
      <i className="mj-story-caustic is-a" />
      <i className="mj-story-caustic is-b" />
      <i className="mj-story-caustic is-c" />

      {phase === 'input' ? (
        <div className="mj-story-composer-input">
          <div className="mj-story-composer-entry">
            <span className="mj-story-spark">
              <SparkGlyph />
            </span>
            <span className="mj-story-prompt">{copy.prompt}</span>
            <span className="mj-story-attach">{copy.attach}</span>
            <span className="mj-story-add">+</span>
          </div>
          <div className="mj-story-source-chips">
            {copy.chips.map((chip, index) => (
              <span
                className={`mj-story-source-chip ${SOURCE_MARKS[index] ?? ''}`}
                key={chip}
              >
                <i />
                <b dir="ltr">{chip}</b>
                <em>×</em>
              </span>
            ))}
          </div>
          <div className="mj-story-composer-foot">
            <span className="mj-story-mini-tabs">
              {copy.tabs.map((tab, index) => (
                <span className={index === 3 ? 'is-active' : ''} key={tab}>
                  <i />
                  {tab}
                </span>
              ))}
            </span>
            <span className="mj-story-mini-cta">{copy.cta}</span>
          </div>
          <p>{copy.next}</p>
        </div>
      ) : null}

      {phase === 'processing' ? (
        <div className="mj-story-processing">
          <div>
            <span>{copy.building}</span>
            <span className="mj-story-processing-icons">
              <i />
              <i />
              <i />
            </span>
          </div>
          <strong>
            <span className="mj-story-pixel-signal" />
            {copy.processing}
          </strong>
          <footer>
            <span className="mj-story-processing-dots">
              <i />
              <i />
              <i />
              <i />
            </span>
            <span className="mj-flow-swell" aria-hidden="true" />
            {copy.keepOpen}
          </footer>
        </div>
      ) : null}

      {phase === 'profile' ? (
        <div className="mj-story-mini-profile">
          <span className="mj-story-profile-label">{copy.ready}</span>
          <div className="mj-story-profile-grid">
            <div>
              <strong>{copy.business}</strong>
              {copy.businessAr ? <small>{copy.businessAr}</small> : null}
              <p>{copy.offer}</p>
            </div>
            <div>
              <span className="mj-story-market">
                <i />
                {copy.market}
              </span>
              <small>{copy.offersLabel}</small>
              <span className="mj-story-offers">
                {copy.offers.map((offer) => (
                  <b key={offer}>{offer}</b>
                ))}
              </span>
              <em>{copy.sources}</em>
            </div>
          </div>
          <footer>
            <span>{copy.ask}</span>
            <span>
              <b>{copy.confirm}</b>
              <i>{copy.wrong}</i>
            </span>
          </footer>
        </div>
      ) : null}
    </div>
  );
}

function MediaSpecimen({
  className = '',
  index = 0,
}: {
  className?: string;
  index?: number;
}): React.ReactElement {
  return (
    <span
      className={`mj-story-media ${className}`}
      data-specimen={String((index % 4) + 1)}
    >
      <i className="mj-story-media-band" />
      <i className="mj-story-media-pixels" />
    </span>
  );
}

function NicheWall({ patterns }: { patterns: readonly string[] }): React.ReactElement {
  return (
    <div className="mj-story-niche-scene">
      <div className="mj-story-patterns">
        {patterns.map((pattern) => (
          <span key={pattern}>{pattern}</span>
        ))}
      </div>
      <div className="mj-story-video-wall">
        {[0, 1, 2].map((column) => (
          <div className={`is-column-${String(column + 1)}`} key={column}>
            {[0, 1, 2, 3].map((row) => (
              <MediaSpecimen index={column + row} key={row} />
            ))}
          </div>
        ))}
        <span className="mj-story-scan" />
      </div>
    </div>
  );
}

function TasteScene({
  copy,
}: {
  copy: (typeof STORY)[keyof typeof STORY];
}): React.ReactElement {
  return (
    <div className="mj-story-taste-scene">
      <div className="mj-story-taste-output">
        <span>{copy.tasteLabel}</span>
        {copy.taste.map((trait) => (
          <b key={trait}>{trait}</b>
        ))}
      </div>
      <div className="mj-story-card-stack">
        <MediaSpecimen className="is-next" index={2} />
        <span className="mj-story-candidate-card">
          <MediaSpecimen index={1} />
          <span className="mj-story-candidate-metrics">
            {copy.metrics.map((metric) => (
              <small key={metric}>{metric}</small>
            ))}
          </span>
          <b>{copy.candidate}</b>
        </span>
        <span className="mj-story-swipe-cue">
          <i />
          <i />
          <i />
          {copy.cue}
        </span>
      </div>
    </div>
  );
}

function ProductionLine({
  copy,
}: {
  copy: (typeof STORY)[keyof typeof STORY];
}): React.ReactElement {
  return (
    <div className="mj-story-line-scene">
      <div className="mj-story-cartridges">
        {copy.inputs.map((input, index) => (
          <span data-input={String(index + 1)} key={input}>
            <i />
            <b>{input}</b>
          </span>
        ))}
      </div>
      <span className="mj-story-intake" />
      <span className="mj-story-belt" />
      <span className="mj-story-lever">
        <i />
        <b />
      </span>
      <span className="mj-story-machine">
        <i className="is-roof" />
        <i className="is-stamp" />
        <i className="is-signal">
          <b />
          <b />
          <b />
        </i>
        <span>M</span>
      </span>
      <span className="mj-story-line-card is-before" />
      <span className="mj-story-line-card is-after">
        <MediaSpecimen index={3} />
      </span>
      <span className="mj-story-check">✓</span>
      <span className="mj-story-rack">
        <i />
        <b>{copy.rack}</b>
      </span>
      <span className="mj-story-active">
        <i />
        {copy.active} · {copy.cadence}
      </span>
      <span className="mj-story-direction">{copy.direction}</span>
    </div>
  );
}

function StoryStage({
  step,
  copy,
  composerPhase,
  walking,
}: {
  step: StoryStep;
  copy: (typeof STORY)[keyof typeof STORY];
  composerPhase: ComposerPhase;
  walking: boolean;
}): React.ReactElement {
  return (
    <div className="mj-story-stage" data-step={String(step + 1)}>
      <div className="mj-story-world" role="img" aria-label={copy.aria[step]}>
        {step === 0 ? (
          <MiniComposer copy={copy.composer} phase={composerPhase} />
        ) : null}
        {step === 1 ? <NicheWall patterns={copy.patterns} /> : null}
        {step === 2 ? <TasteScene copy={copy} /> : null}
        {step === 3 ? <ProductionLine copy={copy} /> : null}
      </div>
      <span
        className={`mj-story-agent${walking ? ' is-walking' : ''}`}
        aria-hidden="true"
      />
      <span className="mj-story-agent-cue" aria-hidden="true">
        <i />
        <b />
      </span>
    </div>
  );
}

export function ProductStorySection(): React.ReactElement {
  const { language } = useLanguage();
  const copy = STORY[language];
  const [selected, setSelected] = useState<StoryStep>(0);
  const [composerPhase, setComposerPhase] = useState<ComposerPhase>('input');
  const [walking, setWalking] = useState(true);
  const buttons = useRef<Array<HTMLButtonElement | null>>([]);

  useEffect(() => {
    setWalking(true);
    const stop = window.setTimeout(() => {
      setWalking(false);
    }, 900);
    return () => {
      window.clearTimeout(stop);
    };
  }, [selected]);

  useEffect(() => {
    if (selected !== 0) return undefined;
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setComposerPhase('profile');
      return undefined;
    }

    let cancelled = false;
    let timers: number[] = [];
    const run = (): void => {
      if (cancelled) return;
      setComposerPhase('input');
      timers = [
        window.setTimeout(() => {
          setComposerPhase('processing');
        }, 3100),
        window.setTimeout(() => {
          setComposerPhase('profile');
        }, 5600),
        window.setTimeout(run, 10300),
      ];
    };
    run();
    return () => {
      cancelled = true;
      timers.forEach((timer) => {
        window.clearTimeout(timer);
      });
    };
  }, [selected]);

  const choose = (next: number): void => {
    setSelected(next as StoryStep);
  };

  return (
    <section
      className="mj-product-story"
      id="product-story"
      data-landing-screen="3"
      aria-labelledby="mj-story-title"
    >
      <div className="mj-product-story-copy">
        <p className="mj-process-eyebrow mj-product-story-fixture">
          <span className="mj-process-eyebrow-dot" aria-hidden="true" />
          {copy.fixture}
        </p>
        <h2 id="mj-story-title">{copy.heading}</h2>
        <p>{copy.sub}</p>
      </div>

      <div className="mj-product-story-experience">
        <StoryStage
          step={selected}
          copy={copy}
          composerPhase={composerPhase}
          walking={walking}
        />

        <div
          className="mj-product-story-steps"
          role="group"
          aria-label={copy.stepsLabel}
        >
          {copy.steps.map((step, index) => {
            const active = index === selected;
            return (
              <button
                key={step}
                ref={(element) => {
                  buttons.current[index] = element;
                }}
                type="button"
                aria-label={step}
                aria-pressed={active}
                onClick={() => {
                  choose(index);
                }}
                onKeyDown={(event) => {
                  const previous = language === 'ar' ? 'ArrowRight' : 'ArrowLeft';
                  const next = language === 'ar' ? 'ArrowLeft' : 'ArrowRight';
                  if (
                    ![previous, next, 'ArrowUp', 'ArrowDown', 'Home', 'End'].includes(
                      event.key,
                    )
                  )
                    return;
                  event.preventDefault();
                  const target =
                    event.key === 'Home'
                      ? 0
                      : event.key === 'End'
                        ? copy.steps.length - 1
                        : event.key === next || event.key === 'ArrowDown'
                          ? (selected + 1) % copy.steps.length
                          : (selected - 1 + copy.steps.length) % copy.steps.length;
                  choose(target);
                  buttons.current[target]?.focus();
                }}
              >
                <span aria-hidden="true" className="mj-story-step-mark" />
                <b dir="ltr">{String(index + 1).padStart(2, '0')}</b>
                <span>{step}</span>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}
